import { Box } from '@rebass/grid';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import {
    ReviewModal_Ticket_Fragment,
    LeaveActivityReviewInput,
    LeaveActivityReviewsFragment,
    LeaveUnauthenticatedActivityReviewInput,
    OrderItemFragment,
    OrderItemFragmentDoc,
    useLeaveActivityReviewMutation,
    useLeaveUnauthenticatedActivityReviewMutation,
} from '../generated/graphql';
import Divider from './Divider';
import { Handler } from './Formed';
import useGetPageSlugByLocale from '../hooks/useGetPageSlugByLocale';
import ActivityReviewForm, { Values } from './ActivityReviewForm';
import ReviewDone from './ReviewDone';
import LeaveReview from './LeaveReview';

interface Props {
    ticket?: ReviewModal_Ticket_Fragment;
    setLeftReview: (val: boolean | null) => void;
    leftReview: boolean | null;
    activity?: LeaveActivityReviewsFragment | null;
    isAnonymous?: boolean;
    loading?: boolean;
}

const LeaveActivityReview: FC<React.PropsWithChildren<Props>> = ({
    ticket,
    leftReview: isReviewDone,
    setLeftReview,
    activity,
    isAnonymous,
    loading,
}) => {
    const [mutate] = useLeaveActivityReviewMutation();
    const [unauthenticatedMutate] = useLeaveUnauthenticatedActivityReviewMutation();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();

    const activityPageSlug = getPageSlugByLocale('ACTIVITY');

    const {
        query: { ticketHash },
        locale,
    } = useRouter();
    const handleSubmit: Handler<Values> = async values => {
        const ratings = {
            ratingActivity: Number(values.ratingActivity),
            ratingHospitality: Number(values.ratingHospitality),
            ratingValueForMoney: Number(values.ratingValueForMoney),
        };
        if (isAnonymous) {
            if (!ticketHash) {
                return;
            }
            await unauthenticatedMutate({
                variables: {
                    input: {
                        ticketHash: ticketHash as string,
                        ...values,
                        ...ratings,
                    } as LeaveUnauthenticatedActivityReviewInput,
                },
            });
            setLeftReview(true);
            return;
        }

        if (!ticket?.id) {
            return;
        }
        await mutate({
            variables: {
                input: {
                    ticketId: ticket.id,
                    ...values,
                    ...ratings,
                    // We know it is, because of Yup
                } as LeaveActivityReviewInput,
            },
            update: (cache, { data: payload }) => {
                const opts = {
                    id: `${ticket?.__typename ?? 'Ticket'}:${ticket.id}`,
                    fragment: OrderItemFragmentDoc,
                    fragmentName: 'OrderItem',
                };
                const old = cache?.readFragment<OrderItemFragment>(opts);

                if (!old || !payload?.leaveActivityReview.activityReview) {
                    return;
                }

                cache.writeFragment({
                    ...opts,
                    data: { ...old, activityReview: payload?.leaveActivityReview.activityReview },
                });
            },
        });
        setLeftReview(true);
    };

    if (isReviewDone) {
        return (
            <ReviewDone
                orderItem={ticket}
                setLeftReview={setLeftReview}
                isAnonymous={isAnonymous}
                pushUrl="/[activity]/[slug]"
                pathName={`/${activityPageSlug}/${activity?.slug}`}
                locale={locale}
            />
        );
    }

    return (
        <>
            <LeaveReview
                item={ticket}
                object={activity}
                webpThumbnail={activity?.webpThumbnail}
                jpegThumbnail={activity?.jpegThumbnail}
                locale={locale}
                isAnonymous={isAnonymous}
                loading={loading}
                pushUrl={`/${activityPageSlug}/${activity?.slug}`}
                strapline={activity?.name ?? undefined}
                subtitle={activity?.name}
            />
            <Box order={2} width={['100%', null, '70%', '60%']}>
                <Divider mt={0} mb={8} />
                <ActivityReviewForm handleSubmit={handleSubmit} activityName={activity?.name} />
            </Box>
        </>
    );
};

export default LeaveActivityReview;
